import type {
  V2Capability,
  V2Model,
  V2ProductVariant,
  V2QualifiedConfiguration,
} from "../types";

export type ProductAvailability =
  | "qualified"
  | "quote_required"
  | "unsupported";

/**
 * Provider-neutral customer presentation supplied by the catalogue API. It is
 * deliberately additive while older deployments still return only readiness
 * fields. Execution must never be inferred from this object alone.
 */
export type ProductCatalogPresentation = NonNullable<
  V2Capability["presentation"]
>;

export interface ProductAvailabilityRecord {
  availability?: ProductAvailability;
  readiness?: ProductAvailability;
  blocked_code?: string;
  media_type?: V2Capability["media_type"];
  customer_available?: boolean;
  test_only?: boolean;
  qualification_scope?: "none" | "full" | "variant";
  qualified_configurations?: V2Capability["qualified_configurations"];
  surface_status?: V2Capability["surface_status"];
  product_variants?: V2Capability["product_variants"];
  presentation?: ProductCatalogPresentation;
}

type VariantAwareCapability = Pick<
  V2Capability,
  | "media_type"
  | "test_only"
  | "qualification_scope"
  | "qualified_configurations"
  | "surface_status"
  | "product_variants"
  | "media_tool"
>;

export type ProductAvailabilityReasonCode =
  | "pending_integration"
  | "catalog_specification_gap"
  | "no_exact_product_variant"
  | "no_production_qualified_variant"
  | "no_production_qualified_configuration"
  | "choose_exact_qualified_configuration"
  | "configuration_missing_variant_binding"
  | "qualified_variant_missing_revision"
  | "variant_pricing_in_qualification"
  | "capability_pricing_in_qualification"
  | "billing_safeguards_in_qualification"
  | "workflow_in_qualification"
  | "production_integration_missing"
  | "unavailable";

const productAvailabilityReasonDefaults = {
  pending_integration:
    "Production integration for this catalogue item is still pending.",
  catalog_specification_gap:
    "The exact settings surface for this capability has not been captured yet.",
  no_exact_product_variant:
    "No exact product variant is bound to this capability.",
  no_production_qualified_variant:
    "No production-qualified product variant is currently available.",
  no_production_qualified_configuration:
    "No production-qualified configuration is currently available.",
  choose_exact_qualified_configuration:
    "Choose an exact qualified configuration before using this capability.",
  configuration_missing_variant_binding:
    "The qualified configuration is not bound to a product variant.",
  qualified_variant_missing_revision:
    "The qualified product variant is not available in this catalog revision.",
  variant_pricing_in_qualification:
    "Pricing for this product variant is still being qualified.",
  capability_pricing_in_qualification:
    "Pricing for this capability is still being qualified.",
  billing_safeguards_in_qualification:
    "Billing safeguards for this capability are still being qualified.",
  workflow_in_qualification:
    "The production workflow for this capability is still being qualified.",
  production_integration_missing:
    "This capability does not yet have a supported production integration.",
  unavailable: "This capability is not currently available.",
} satisfies Record<ProductAvailabilityReasonCode, string>;

/**
 * Returns the one qualification record that can be bound without asking the
 * customer to choose between multiple execution configurations.
 */
export function soleQualifiedConfiguration(
  capability: Pick<V2Capability, "qualified_configurations">,
): V2QualifiedConfiguration | undefined {
  return capability.qualified_configurations.length === 1
    ? capability.qualified_configurations[0]
    : undefined;
}

/** Resolve an opaque product variant without accepting duplicate IDs. */
export function productVariantByID(
  capability: Pick<V2Capability, "product_variants">,
  productVariantID: string | undefined,
): V2ProductVariant | undefined {
  const id = productVariantID?.trim();
  if (!id) return undefined;

  const matches = (capability.product_variants ?? []).filter(
    (variant) => variant.id === id,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

/**
 * Resolve the provider-neutral product surface bound to the sole qualified
 * configuration. Missing, stale, and ambiguous bindings all fail closed.
 */
export function qualifiedProductVariant(
  capability: Pick<
    V2Capability,
    "qualified_configurations" | "product_variants" | "media_tool"
  >,
): V2ProductVariant | undefined {
  if (capability.media_tool) return capability.media_tool.execution_available && capability.media_tool.quote_available
    ? productVariantByID(capability, capability.media_tool.variant_id) : undefined;
  const configuration = soleQualifiedConfiguration(capability);
  return productVariantByID(capability, configuration?.product_variant_id);
}

export function productVariantIsQualified(
  capability: Pick<
    V2Capability,
    "qualified_configurations" | "product_variants"
  >,
  variant: V2ProductVariant | string,
): boolean {
  const candidateID = typeof variant === "string" ? variant : variant.id;
  return qualifiedProductVariant(capability)?.id === candidateID;
}

/** Exact, finite top-level generation counts admitted by one product variant. */
export function productVariantOutputCounts(
  variant: V2ProductVariant | undefined,
): number[] {
  if (!variant) return [];
  const setting = variant.output_count;
  if (!setting) {
    return Array.from(
      { length: Math.max(1, variant.max_outputs ?? 1) },
      (_, index) => index + 1,
    );
  }
  const explicit = [
    ...(setting.numeric_enum ?? []),
    ...(setting.enum_values ?? []),
  ]
    .filter(
      (value): value is number =>
        typeof value === "number" && Number.isSafeInteger(value) && value >= 1,
    );
  const unique = [...new Set(explicit)].filter(
    (value) => value <= (variant.max_outputs ?? 1),
  );
  if (unique.length > 0) return unique.sort((left, right) => left - right);

  const minimum = Math.max(1, Math.ceil(setting.minimum ?? 1));
  const maximum = Math.max(
    minimum,
    Math.floor(
      Math.min(
        setting.maximum ?? variant.max_outputs ?? 1,
        variant.max_outputs ?? 1,
      ),
    ),
  );
  const step = Math.max(1, Math.floor(setting.step ?? 1));
  const values: number[] = [];
  for (let value = minimum; value <= maximum; value += step) values.push(value);
  return values;
}

export function productVariantDefaultOutputCount(
  variant: V2ProductVariant | undefined,
): number {
  const allowed = productVariantOutputCounts(variant);
  const declared = variant?.output_count?.default;
  return typeof declared === "number" && allowed.includes(declared)
    ? declared
    : (allowed[0] ?? 1);
}

/**
 * Explain why an otherwise qualified catalog row cannot be bound to one exact
 * provider-neutral product surface. This copy is safe for customer UI.
 */
export function productVariantGapExplanation(
  capability: VariantAwareCapability,
): string | undefined {
  const reason = productVariantGapReasonCode(capability);
  return reason ? productAvailabilityReasonDefaults[reason] : undefined;
}

function productVariantGapReasonCode(
  capability: VariantAwareCapability,
): ProductAvailabilityReasonCode | undefined {
  if (capability.media_tool) return qualifiedProductVariant(capability) ? undefined : "no_production_qualified_variant";
  if (capability.surface_status === "gap") {
    return "catalog_specification_gap";
  }

  // Non-catalog Audio and local visual-preview fixtures remain on the legacy
  // full-qualification contract. Production Image/Video rows fail closed.
  if (
    (capability.media_type === "audio" || capability.test_only) &&
    capability.qualification_scope === "full" &&
    (capability.product_variants?.length ?? 0) === 0
  ) {
    return undefined;
  }

  if (capability.qualification_scope === "full") {
    return "no_exact_product_variant";
  }
  if (capability.qualification_scope !== "variant") {
    return "no_production_qualified_variant";
  }
  if (capability.qualified_configurations.length === 0) {
    return "no_production_qualified_configuration";
  }
  if (capability.qualified_configurations.length > 1) {
    return "choose_exact_qualified_configuration";
  }

  const configuration = capability.qualified_configurations[0];
  if (!configuration?.product_variant_id?.trim()) {
    return "configuration_missing_variant_binding";
  }

  const variant = productVariantByID(
    capability,
    configuration.product_variant_id,
  );
  if (!variant) {
    return "qualified_variant_missing_revision";
  }
  if (variant.quote_policy !== "exact") {
    return "variant_pricing_in_qualification";
  }
  return undefined;
}

/**
 * Public catalog copy is deliberately derived from product state, not from
 * integration diagnostics. Supplier names, route identifiers, and private
 * quote evidence must stay behind the API boundary.
 */
export function productAvailabilityExplanation(
  item: ProductAvailabilityRecord,
): string | undefined {
  const reason = productAvailabilityReasonCode(item);
  return reason ? productAvailabilityReasonDefaults[reason] : undefined;
}

export function productAvailabilityReasonCode(
  item: ProductAvailabilityRecord,
): ProductAvailabilityReasonCode | undefined {
  const state = item.availability ?? item.readiness;
  const code = item.blocked_code?.trim().toLocaleLowerCase() ?? "";
  if (item.presentation?.status === "pending_integration") {
    return "pending_integration";
  }
  if (item.surface_status === "gap" || code === "catalog_specification_gap") {
    return "catalog_specification_gap";
  }
  if (
    state === "qualified" &&
    (item.customer_available || item.test_only) &&
    item.media_type &&
    item.qualification_scope &&
    item.qualified_configurations
  ) {
    return productVariantGapReasonCode({
      media_type: item.media_type,
      test_only: item.test_only ?? false,
      qualification_scope: item.qualification_scope,
      qualified_configurations: item.qualified_configurations,
      surface_status: item.surface_status,
      product_variants: item.product_variants,
    });
  }
  if (
    state === "qualified" &&
    (item.customer_available || item.test_only) &&
    (item.qualification_scope === undefined ||
      item.qualification_scope === "full" ||
      (item.qualification_scope === "variant" &&
        item.qualified_configurations?.length === 1))
  ) {
    return undefined;
  }

  if (
    state === "qualified" &&
    item.qualification_scope === "variant" &&
    (item.qualified_configurations?.length ?? 0) === 0
  ) {
    return "no_production_qualified_configuration";
  }
  if (
    state === "qualified" &&
    item.qualification_scope === "variant" &&
    (item.qualified_configurations?.length ?? 0) > 1
  ) {
    return "choose_exact_qualified_configuration";
  }

  if (
    state === "quote_required" ||
    code.includes("quote") ||
    code.includes("price") ||
    code.includes("tariff")
  ) {
    return "capability_pricing_in_qualification";
  }
  if (code.includes("settlement") || code.includes("billing")) {
    return "billing_safeguards_in_qualification";
  }
  if (
    code.includes("workflow") ||
    code.includes("transport") ||
    code.includes("delivery")
  ) {
    return "workflow_in_qualification";
  }
  if (
    state === "unsupported" ||
    code.includes("integration") ||
    code.includes("supplier") ||
    code.includes("mapping")
  ) {
    return "production_integration_missing";
  }
  return "unavailable";
}

export function humanizeProductOperation(value: string): string {
  return value
    .replaceAll(/[_-]+/g, " ")
    .replace(/(^|\s)\S/g, (character) => character.toUpperCase());
}

/**
 * Keep the catalogue inspectable when the narrower model projection is empty
 * or temporarily behind the capability projection. Legacy executable routes
 * still need their model projection. Media tools carry exact server admission
 * independently; other synthesized rows remain presentation-only.
 */
export function mergeCatalogModels(
  models: readonly V2Model[],
  capabilities: readonly V2Capability[],
  mediaType: "image" | "video",
): V2Model[] {
  const merged = models.filter((model) => model.media_type === mediaType).map(model => {
    const tool = capabilities.find(capability => capability.product_model_id === model.id && capability.media_tool && capabilityIsExecutable(capability));
    if (!tool) return model;
    // The legacy projection can lag behind exact media-tool admission.
    return { ...model, availability: tool.readiness, blocked_code: tool.blocked_code,
      customer_available: tool.customer_available, test_only: tool.test_only, presentation: tool.presentation,
      credits_per_output: tool.credits_per_output, minimum_credits_per_output: tool.minimum_credits_per_output,
      maximum_credits_per_output: tool.maximum_credits_per_output };
  });
  const seen = new Set(merged.map((model) => model.id));
  const namesByProductID = new Map(
    merged.map((model) => [model.product_id, model.name]),
  );

  for (const capability of capabilities) {
    if (
      capability.media_type !== mediaType ||
      seen.has(capability.product_model_id) ||
      (capabilityIsExecutable(capability) && !capability.media_tool)
    ) {
      continue;
    }
    const productName =
      namesByProductID.get(capability.product_id) ??
      capability.media_tool?.name ?? catalogProductName(capability.product_id || capability.product_model_id);
    merged.push({
      id: capability.product_model_id,
      product_id: capability.product_id,
      name: productName,
      kind: capability.kind,
      media_type: capability.media_type,
      operation: capability.operation,
      operations: capability.operations,
      availability: capability.readiness,
      blocked_code: capability.blocked_code,
      customer_available: capability.customer_available,
      test_only: capability.test_only,
      credits_per_output: capability.credits_per_output,
      minimum_credits_per_output: capability.minimum_credits_per_output,
      maximum_credits_per_output: capability.maximum_credits_per_output,
      supports_source_assets: Boolean(
        capability.source_assets ||
          capability.source_images ||
          capability.source_videos ||
          capability.source_audio ||
          capability.max_source_assets > 0,
      ),
      unavailable_reason: productAvailabilityExplanation(capability),
    });
    seen.add(capability.product_model_id);
    namesByProductID.set(capability.product_id, productName);
  }

  return merged;
}

function catalogProductName(identifier: string): string {
  const family = identifier.split(":", 1)[0]?.split(".").at(-1) ?? identifier;
  return humanizeProductOperation(family);
}

/** Stable UI identity for a product family operation without duplicating an
 * operation suffix when the catalog ID is already composite. */
export function productSelectionKey(id: string, operation: string): string {
  const suffix = `:${operation}`;
  return id.endsWith(suffix) ? id : `${id}${suffix}`;
}

/**
 * A catalog capability can describe more configurations than Afterform is
 * currently willing to execute. Keep that product discovery surface separate
 * from the exact configuration used for quoting and generation.
 */
export function capabilityIsExecutable(
  capability: Pick<
    V2Capability,
    | "readiness"
    | "media_type"
    | "customer_available"
    | "test_only"
    | "qualification_scope"
    | "qualified_configurations"
    | "surface_status"
    | "product_variants"
    | "media_tool"
  > & {
    presentation?: ProductCatalogPresentation;
  },
): boolean {
  if (capability.media_tool) return Boolean(qualifiedProductVariant(capability));
  // `presentation` describes catalogue state; it is never an admission grant. When
  // it is present, anything short of the backend's explicit generate action
  // fails closed even if legacy readiness fields are accidentally broader.
  if (
    capability.presentation &&
    (capability.presentation.status !== "available" ||
      capability.presentation.action !== "generate")
  ) {
    return false;
  }
  if (
    capability.readiness !== "qualified" ||
    (!capability.customer_available && !capability.test_only)
  ) {
    return false;
  }
  return productVariantGapExplanation(capability) === undefined;
}

/**
 * Project one catalog variant's provider-neutral settings and reference
 * topology onto a capability for inspection. This does not mark the variant
 * qualified or change any admission fields.
 */
export function projectProductVariantCapability(
  capability: V2Capability,
  variant: V2ProductVariant | string,
): V2Capability | undefined {
  const variantID = typeof variant === "string" ? variant : variant.id;
  const selected = productVariantByID(capability, variantID);
  if (!selected) return undefined;

  return projectCapabilityInputSurface(
    capability,
    selected.settings,
    selected.reference_inputs,
    selected,
  );
}

/**
 * Returns the provider-neutral capability that may actually be sent to the
 * quote endpoint. Variant-qualified routes are narrowed to one immutable
 * qualified configuration; no union is constructed because combining two
 * independently qualified variants could create an unqualified cross-product.
 */
export function qualifiedExecutionCapability(
  capability: V2Capability | undefined,
): V2Capability | undefined {
  if (!capability || !capabilityIsExecutable(capability)) return undefined;
  if (capability.media_tool) return projectProductVariantCapability(capability, capability.media_tool.variant_id);
  if (capability.qualification_scope === "full") return capability;

  const configuration = soleQualifiedConfiguration(capability);
  if (!configuration) return undefined;

  const variant = qualifiedProductVariant(capability);
  if (!variant) return undefined;

  const settings = configuration.settings;
  const referenceInputs = configuration.reference_inputs;
  const narrowed = projectCapabilityInputSurface(
    capability,
    settings,
    referenceInputs,
    variant,
  );
  return {
    ...narrowed,
    qualified_configurations: [configuration],
    max_outputs: Math.min(
      capability.max_outputs,
      configuration.max_outputs,
      variant.max_outputs ?? 1,
    ),
    credits_per_output: configuration.credits_per_output,
  };
}

function projectCapabilityInputSurface(
  capability: V2Capability,
  settings: readonly QualifiedSetting[],
  referenceInputs: Readonly<NonNullable<V2Capability["reference_inputs"]>>,
  variant?: V2ProductVariant,
): V2Capability {
  const roles = uniqueStrings(
    referenceInputs.map((reference) =>
      normalizeReferenceRole(reference.role ?? reference.key),
    ),
  );
  const maxSourceAssets = referenceInputs.reduce(
    (total, reference) => total + Math.max(0, reference.max_items),
    0,
  );
  const supportsMediaKind = (mediaKind: "image" | "video" | "audio") =>
    referenceInputs.some((reference) => {
      const candidate = reference.media_kind ?? "image";
      return candidate === mediaKind || candidate === "mixed";
    });
  const hasReferenceRole = (role: string) =>
    referenceInputs.some(
      (reference) =>
        normalizeReferenceRole(reference.role ?? reference.key) === role,
    );
  const requiresReferenceRole = (role: string) =>
    referenceInputs.some(
      (reference) =>
        reference.required &&
        normalizeReferenceRole(reference.role ?? reference.key) === role,
    );

  return {
    ...capability,
    max_outputs: variant
      ? Math.min(capability.max_outputs, variant.max_outputs ?? 1)
      : capability.max_outputs,
    settings: [...settings],
    reference_inputs: [...referenceInputs],
    max_source_assets:
      capability.max_source_assets > 0
        ? Math.min(capability.max_source_assets, maxSourceAssets)
        : maxSourceAssets,
    source_assets: referenceInputs.length > 0,
    reference_roles: roles,
    aspect_ratios: stringSettingValues(settings, "aspect_ratio", "ratio", ...(capability.media_tool ? ["size"] : [])),
    output_formats: stringSettingValues(settings, "output_format", "format"),
    resolutions: stringSettingValues(
      settings,
      "resolution",
      ...(capability.media_tool ? ["target_resolution", "output_resolution"] : ["size", "image_size"]),
    ),
    thinking_levels: stringSettingValues(
      settings,
      "thinking_level",
      "thinking_mode",
    ),
    style_options: stringSettingValues(settings, "style", "visual_type"),
    quality_levels: stringSettingValues(settings, "quality", "tier", "mode"),
    durations_seconds: numericSettingValues(
      settings,
      "duration",
      "duration_seconds",
      "length_seconds",
    ),
    frame_rates: numericSettingValues(settings, "frame_rate", "fps"),
    audio_behaviors: stringSettingValues(
      settings,
      "audio_behavior",
      "sound",
      "native_audio",
      "music",
    ),
    source_images: supportsMediaKind("image"),
    source_videos: supportsMediaKind("video"),
    source_audio: supportsMediaKind("audio"),
    supports_seed: hasSetting(settings, "seed", "image_seed", "model_seed"),
    supports_first_frame: hasReferenceRole("first_frame"),
    requires_first_frame: requiresReferenceRole("first_frame"),
    supports_last_frame: hasReferenceRole("last_frame"),
    requires_last_frame: requiresReferenceRole("last_frame"),
    supports_references: referenceInputs.length > 0,
    supports_camera_motion: hasSetting(
      settings,
      "camera_motion",
      "motion",
      "camera_fixed",
    ),
    supports_negative_instruction: hasSetting(
      settings,
      "negative_prompt",
      "negative_instruction",
    ),
    supports_native_audio: hasSetting(
      settings,
      "generate_audio",
      "native_audio",
      "sound",
    ),
    supports_multi_shot: hasSetting(
      settings,
      "multi_prompt",
      "storyboard",
      "shots",
    ),
  };
}

type QualifiedConfiguration = V2Capability["qualified_configurations"][number];
type QualifiedSetting = QualifiedConfiguration["settings"][number];

function hasSetting(settings: readonly QualifiedSetting[], ...keys: string[]) {
  return settings.some((setting) => keys.includes(setting.key));
}

function stringSettingValues(
  settings: readonly QualifiedSetting[],
  ...keys: string[]
): string[] {
  const values: string[] = [];
  for (const setting of settings) {
    if (!keys.includes(setting.key)) continue;
    values.push(...(setting.enum ?? []));
    values.push(
      ...(setting.enum_values ?? []).filter(
        (value): value is string => typeof value === "string",
      ),
    );
    if (typeof setting.default === "string") values.push(setting.default);
  }
  return uniqueStrings(values);
}

function numericSettingValues(
  settings: readonly QualifiedSetting[],
  ...keys: string[]
): number[] {
  const values: number[] = [];
  for (const setting of settings) {
    if (!keys.includes(setting.key)) continue;
    values.push(...(setting.numeric_enum ?? []));
    values.push(
      ...(setting.enum_values ?? []).filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value),
      ),
    );
    if (
      typeof setting.default === "number" &&
      Number.isFinite(setting.default)
    ) {
      values.push(setting.default);
    }
  }
  return [...new Set(values)];
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function normalizeReferenceRole(value: string): string {
  const role = value.trim().toLocaleLowerCase().replaceAll("-", "_");
  if (["start", "start_frame", "first_image", "start_image"].includes(role)) {
    return "first_frame";
  }
  if (["end", "end_frame", "last_image", "end_image"].includes(role)) {
    return "last_frame";
  }
  if (["video", "input_video"].includes(role)) return "source_video";
  if (["audio", "input_audio"].includes(role)) return "source_audio";
  return role;
}
